import { Injectable } from '@angular/core';

import { BetslipConfig, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { UserActions } from '@frontend/sports/user/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, debounceTime, delay, filter, map, tap, withLatestFrom } from 'rxjs';

import { RewardTokenType } from '../../../../lib/tokens-base/token-base.models';
import { betslipBaseStateSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipType } from '../../../core/betslip-type';
import { PickId, pickIdFactory } from '../../../core/picks/pick-id';
import BetbuilderActions from '../../bet-builder/actions';
import { IBetBuilderBetState } from '../../bet-builder/models';
import BetslipComboBetActions from '../../combo-bet/actions';
import { selectBetBuilderTypeState } from '../../linear-bet-builder/selectors';
import { ensureLinearMode } from '../../linear/helpers';
import BetslipPickActions from '../../picks/actions';
import { addGroup, replaceGroup } from '../../picks/grouped-picks.actions';
import { betslipPickStateSelector, betslipPicksListSelector } from '../../picks/selectors';
import { QuickBetActions } from '../../quick-bet/quick-bet-actions';
import { quickBetUiStateSelector } from '../../quick-bet/quick-bet.selectors';
import { QuickBetUiState } from '../../quick-bet/quick-bet.state';
import BetslipSingleBetActions from '../../single-bet/actions';
import { singleBetStateSelector } from '../../single-bet/selectors';
import { ISingleBetState } from '../../single-bet/state';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { BetslipTypesActions } from '../../types/actions';
import { betslipTypeStateSelector, selectBetBuilderPicksState, selectHasBetBuilderStatePicks } from '../../types/selectors';
import { RewardTokensActions } from '../actions';
import { CriteriaType, IRewardToken } from '../reward-tokens.model';
import {
    selectAllAssignedTokens,
    selectRewardTokens,
    selectTokenForContextFactory,
    selectTokensEligibilityState,
    selectTokensStateContext,
} from '../selectors';
import {
    getAllSelectedTokens,
    getSelectedAndEligibleAccaBoost,
    getSelectedTokenState,
    getTokenSelectionContext,
} from '../services/linear-reward-tokens.utils';
import { RewardTokensDataService } from '../services/reward-tokens-data.service';
import { RewardTokensValidationService } from '../services/reward-tokens-validation.service';
import { TokenValidationInput } from '../services/validators/criteria-validator-base.service';
import {
    getBetBuilderValidationInput,
    getComboBetValidationInput,
    getSingleBetValidationInput,
    getTeaserBetValidationInput,
    selectBetBuilderValidationInputState,
    selectComboValidationInputState,
    selectSingleValidationInputState,
    selectTeaserValidationInputState,
} from '../services/validators/utils/state-extract.helper';
import { IRewardTokenEligibility, TokensRecord } from '../state';

@Injectable()
export class RewardTokensEffects {
    private expiredOrDeactivatedError: string;

    constructor(
        private validationService: RewardTokensValidationService,
        private toaster: ToasterService,
        private betslipConfig: BetslipConfig,
        private rewardTokensDataService: RewardTokensDataService,
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        protected crmConfig: CrmConfig,
        sitecore: Sitecore,
    ) {
        this.expiredOrDeactivatedError = sitecore.crmTokens.errors.ExpiredOrDeactivated;
    }

    static COMMON_CONTEXT_CHANGE_ACTIONS = [
        BetslipPickActions.togglePick,
        BetslipPickActions.toggleAllPicks,
        BetslipPickActions.updateV1Picks,
        BetslipPickActions.updateV2Picks,
        BetslipPickActions.updateGroupPicks,
        BetslipPickActions.setStartingPrice,
        BetslipPickActions.setFixedPrice,
        BetslipPickActions.changeSelection,
        BetslipPickActions.changeLockState,
        BetslipPickActions.replacePick,
        replaceGroup,
        addGroup,
        BetslipPickActions.removeTypePicks,
        RewardTokensActions.setTokens,
        RewardTokensActions.setToken,
        RewardTokensActions.addTokens,
        RewardTokensActions.removeTokensByType,
        RewardTokensActions.removeTokensById,
        BetslipTypesActions.populateFromStorage,
    ];

    static SINGLE_BET_CONTEXT_CHANGE_ACTIONS = [
        BetslipSingleBetActions.removePick,
        BetslipSingleBetActions.setSingleBetStake,
        BetslipSingleBetActions.toggleSingleBetPick,
        BetslipSingleBetActions.setSingleBetPickEachWay,
        BetslipSingleBetActions.setSingleBetPicksEachWay,
        BetslipSingleBetActions.toggleSingleBetPickEachWay,
        BetslipSingleBetActions.setSingleBetPickStake,
        QuickBetActions.setStakeState,
    ];

    static BET_BUILDER_CONTEXT_CHANGE_ACTIONS = [
        BetbuilderActions.addPick,
        BetbuilderActions.removePick,
        BetbuilderActions.removeIncorrectSelection,
        BetbuilderActions.setPickStake,
        BetbuilderActions.setPickStakes,
        BetbuilderActions.updateBetBuilderPick,
    ];

    static COMBO_BET_CONTEXT_CHANGE_ACTIONS = [
        BetslipComboBetActions.removePick,
        BetslipComboBetActions.replacePick,
        BetslipComboBetActions.setComboBetStake,
        BetslipComboBetActions.setComboEachWay,
        BetslipComboBetActions.toggleComboBetPick,
    ];

    static TEASER_BET_CONTEXT_CHANGE_ACTIONS = [
        BetslipTeaserActions.removePick,
        BetslipTeaserActions.removePicks,
        BetslipTeaserActions.setTeasePicks,
        BetslipTeaserActions.addUpdatePick,
        BetslipTeaserActions.togglePick,
        BetslipTeaserActions.toggleAllPicks,
    ];

    static BETSLIP_CONTEXT_CHANGE_ACTIONS = [
        ...RewardTokensEffects.COMMON_CONTEXT_CHANGE_ACTIONS,
        ...RewardTokensEffects.SINGLE_BET_CONTEXT_CHANGE_ACTIONS,
        ...RewardTokensEffects.COMBO_BET_CONTEXT_CHANGE_ACTIONS,
        ...RewardTokensEffects.TEASER_BET_CONTEXT_CHANGE_ACTIONS,
        ...RewardTokensEffects.BET_BUILDER_CONTEXT_CHANGE_ACTIONS,
    ];

    checkComboEligibilityEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...RewardTokensEffects.COMMON_CONTEXT_CHANGE_ACTIONS, ...RewardTokensEffects.COMBO_BET_CONTEXT_CHANGE_ACTIONS),
            debounceTime(3),
            withLatestFrom(this.store.select(selectComboValidationInputState)),
            filter(([, { comboBet }]) => Object.keys(comboBet.picks).length > 0),
            map(([_, { tokens, comboBet, pickList }]) => {
                const comboBetInput = getComboBetValidationInput(comboBet, pickList);
                const comboBetEligibilityState = this.validationService.getTokenEligibilityStates(tokens, comboBetInput);

                return RewardTokensActions.setComboBetEligibility({ payload: comboBetEligibilityState });
            }),
        );
    });

    checkTeaserEligibilityEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...RewardTokensEffects.COMMON_CONTEXT_CHANGE_ACTIONS, ...RewardTokensEffects.TEASER_BET_CONTEXT_CHANGE_ACTIONS),
            withLatestFrom(this.store.select(selectTeaserValidationInputState)),
            filter(([, { teaserBet }]) => Object.keys(teaserBet.picks).length > 0),
            map(([_, { tokens, teaserBet, pickList }]) => {
                const teaserBetInput = getTeaserBetValidationInput(teaserBet, pickList);
                const teaserBetEligibilityState = this.validationService.getTokenEligibilityStates(tokens, teaserBetInput);

                return RewardTokensActions.setTeaserBetEligibility({ payload: teaserBetEligibilityState });
            }),
        );
    });

    checkSingleEligibilityEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...RewardTokensEffects.COMMON_CONTEXT_CHANGE_ACTIONS, ...RewardTokensEffects.SINGLE_BET_CONTEXT_CHANGE_ACTIONS),
            debounceTime(3),
            withLatestFrom(this.store.select(selectSingleValidationInputState)),
            map(([_, { tokens, singleBet, pickList }]) => {
                const eligibilities = this.getPickEligibilities(
                    singleBet.picks,
                    (pickId) => getSingleBetValidationInput(singleBet, pickList, pickId),
                    tokens,
                );

                return RewardTokensActions.setSingleBetEligibilies({
                    payload: eligibilities,
                });
            }),
        );
    });

    checkBetBuilderEligibilityEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...RewardTokensEffects.COMMON_CONTEXT_CHANGE_ACTIONS, ...RewardTokensEffects.BET_BUILDER_CONTEXT_CHANGE_ACTIONS),
            ensureLinearMode(this.store),
            debounceTime(3),
            withLatestFrom(this.store.select(selectBetBuilderValidationInputState)),
            map(([_, { tokens, betBuilder, pickList }]) => {
                const eligibilities = this.getPickEligibilities(
                    betBuilder.picks,
                    (pickId) => getBetBuilderValidationInput(betBuilder, pickList, pickId),
                    tokens,
                );

                return RewardTokensActions.setBetBuilderEligibilities({
                    payload: eligibilities,
                });
            }),
        );
    });

    betslipContextChangedEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...RewardTokensEffects.BETSLIP_CONTEXT_CHANGE_ACTIONS),
            //debounce time was added to clean up reward token effects loop.
            //these effects are independent and having a debounce will not affect the functionality of any module.
            debounceTime(3),
            map(() => RewardTokensActions.notifyEligibilityUpdated()),
        );
    });

    unselectIneligibleTokensForSingleBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.setSingleBetEligibilies),
            debounceTime(3),
            withLatestFrom(this.store.select(selectTokensStateContext), this.store.select(betslipBaseStateSelector)),
            concatMap(([_, { eligibilityState, tokens, types }, betslipState]) => {
                return this.unselectInelgibleTokensForPicks(
                    types.singleBet.picks,
                    eligibilityState.singleBet,
                    tokens,
                    BetslipSingleBetActions.setSingleBetPickRewardToken,
                    this.handleTokenExpiryDialog,
                    betslipState === BetslipState.Result,
                );
            }),
        );
    });

    unselectIneligibleTokensForBetBuilder$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.setBetBuilderEligibilities),
            debounceTime(3),
            withLatestFrom(this.store.select(selectTokensStateContext), this.store.select(betslipBaseStateSelector)),
            concatMap(([_, { eligibilityState, tokens, types }, betslipState]) => {
                return this.unselectInelgibleTokensForPicks(
                    types.betBuilder.picks,
                    eligibilityState.betBuilder,
                    tokens,
                    BetbuilderActions.setPickRewardToken,
                    this.handleTokenExpiryDialog,
                    betslipState === BetslipState.Result,
                );
            }),
        );
    });

    unselectIneligibleTokensForComboBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.setComboBetEligibility),
            debounceTime(3),
            withLatestFrom(
                this.store.select(selectTokensStateContext),
                this.store.select(betslipBaseStateSelector),
                this.store.select(quickBetUiStateSelector),
            ),
            filter(([, { types }]) => !!types.comboBet.rewardTokenId),
            map(([_, { eligibilityState, tokens, types }, betslipState, quickBetState]) => ({
                ...getSelectedTokenState(types.comboBet.rewardTokenId!, tokens, eligibilityState.comboBet),
                betslipState,
                quickBetState,
            })),
            filter(
                ({ token, tokenEligibility }) => !token || !tokenEligibility || tokenEligibility.invalidHardCriteria === CriteriaType.EachWaySelected,
            ),
            tap(({ betslipState, tokenEligibility, quickBetState }) => {
                this.handleTokenExpiryDialog(betslipState === BetslipState.Result || quickBetState === QuickBetUiState.Success, tokenEligibility);
            }),
            map(() => BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null })),
        );
    });

    unselectIneligibleTokensForTeaserBet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.setTeaserBetEligibility),
            debounceTime(3),
            withLatestFrom(this.store.select(selectTokensStateContext), this.store.select(betslipBaseStateSelector)),
            filter(([, { types }]) => !!types.teaserBet.rewardTokenId),
            map(([_, { eligibilityState, tokens, types }, betslipState]) => ({
                ...getSelectedTokenState(types.teaserBet.rewardTokenId!, tokens, eligibilityState.teaserBet),
                betslipState,
            })),
            filter(
                ({ token, tokenEligibility }) => !token || !tokenEligibility || tokenEligibility.invalidHardCriteria === CriteriaType.EachWaySelected,
            ),
            tap(({ betslipState, tokenEligibility }) => {
                this.handleTokenExpiryDialog(betslipState === BetslipState.Result, tokenEligibility);
            }),
            map(() => BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: null })),
        );
    });

    toggleTokenDialogCloseEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.toggleToken),
            filter(() => !this.crmConfig.tokens.showApplyRemoveButtons),
            map(() => RewardTokensActions.closeDialog()),
        );
    });

    toggleTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.toggleToken),
            concatLatestFrom(() => [this.store.select(betslipTypeStateSelector)]),
            map(([action, types]) => {
                const context = getTokenSelectionContext(action.payload.tokenId, types);

                if (action.payload.betslipType === context?.betslipType && action.payload.pickId === context?.pickId) {
                    return RewardTokensActions.deselectToken({ payload: action.payload });
                }

                return RewardTokensActions.selectToken({ payload: action.payload });
            }),
        );
    });

    deselectTokenForContextEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectTokenForContext),
            concatLatestFrom((a) => this.store.select(selectTokenForContextFactory(a.payload))),
            filter(([, token]) => !!token),
            map(([action, token]) =>
                RewardTokensActions.deselectToken({
                    payload: {
                        betslipType: action.payload.betslipType,
                        pickId: action.payload.pickId,
                        tokenId: token!.userTokenId,
                    },
                }),
            ),
        );
    });

    unselectTokensOnPickRemovalEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks, BetslipPickActions.removeWebAppPick),
            debounceTime(3),
            concatLatestFrom(() => [this.store.select(betslipPickStateSelector), this.store.select(selectHasBetBuilderStatePicks)]),
            concatMap(function* ([_, picks]): Iterable<Action> {
                if (picks.pickList.length < 2) {
                    yield BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null });
                    yield BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: null });
                }

                if (picks.pickList.length === 0) {
                    yield RewardTokensActions.deselectAllToken();
                    yield RewardTokensActions.closeDialog();
                }
            }),
        );
    });

    handleSelectedTokensRemovalEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.removeSelectedTokens),
            concatLatestFrom(() => [this.store.select(selectRewardTokens), this.store.select(betslipTypeStateSelector)]),
            map(([_, tokens, types]) => getAllSelectedTokens(tokens, types).map((t) => t.userTokenId)),
            filter((selectedTokens) => selectedTokens.length > 0),
            map((selectedTokens) => RewardTokensActions.removeTokensById({ userTokenIds: selectedTokens })),
        );
    });

    cleanErrorsOnSelectedTokenChangeEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.selectToken, RewardTokensActions.deselectAllToken),
            map(() => QuickBetActions.clearWarnings()),
        );
    });

    handleSelectRewardTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.selectToken),
            concatLatestFrom(() => [
                this.store.select(singleBetStateSelector),
                this.store.select(selectRewardTokens),
                this.store.select(selectBetBuilderPicksState),
            ]),
            filter(([action, , tokens]) => tokens[action.payload.tokenId].rewardTokenType !== RewardTokenType.Eds),
            concatMap(([{ payload }, singleBetState, _, betBuilderState]) => {
                switch (payload.betslipType) {
                    case BetslipType.Single:
                        return this.setTokenForPick(
                            payload.pickId,
                            singleBetState.picks,
                            payload.tokenId,
                            BetslipSingleBetActions.setSingleBetPickRewardToken,
                        );
                    case BetslipType.BetBuilder:
                        return this.setTokenForPick(payload.pickId, betBuilderState.picks, payload.tokenId, BetbuilderActions.setPickRewardToken);
                    case BetslipType.Combo:
                        return [BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: payload.tokenId })];
                    case BetslipType.Teaser:
                        return [BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: payload.tokenId })];
                }

                return [];
            }),
        );
    });

    handleDeselectRewardTokenForSingleBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectToken),
            withLatestFrom(this.store.select(singleBetStateSelector)),
            filter(([action]) => action.payload.betslipType === BetslipType.Single),
            map(([action, singleBet]): [string | undefined, ISingleBetState] => [action.payload.pickId?.toString(), singleBet]),
            filter(([pickId, singleBet]) => !!pickId && Object.keys(singleBet.picks).includes(pickId)),
            map(([pickId]) =>
                BetslipSingleBetActions.setSingleBetPickRewardToken({
                    pickId: pickId!,
                    rewardTokenId: null,
                }),
            ),
        );
    });

    handleDeselectRewardTokenForBetBuilderEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectToken),
            withLatestFrom(this.store.select(selectBetBuilderTypeState)),
            filter(([action]) => action.payload.betslipType === BetslipType.BetBuilder),
            map(([action, betBuilder]): [string | undefined, IBetBuilderBetState] => [action.payload.pickId?.toString(), betBuilder]),
            filter(([pickId, betBuilder]) => !!pickId && Object.keys(betBuilder.picks).includes(pickId)),
            map(([pickId]) => BetbuilderActions.setPickRewardToken({ pickId: pickId!, rewardTokenId: null })),
        );
    });

    handleDeselectRewardTokenForComboBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectToken),
            filter((action) => action.payload.betslipType === BetslipType.Combo),
            map(() => BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null })),
        );
    });

    handleDeselectRewardTokenForTeaserBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectToken),
            filter((action) => action.payload.betslipType === BetslipType.Teaser),
            map(() => BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: null })),
        );
    });

    // Handles deselection of tokens when the same token is selected in a different reward token context
    handleAutoDeselectRewardTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.selectToken),
            concatLatestFrom(() => [
                this.store.select(betslipTypeStateSelector),
                this.store.select(betslipPicksListSelector),
                this.store.select(selectAllAssignedTokens),
            ]),
            filter(([{ payload }, , , assignedTokens]) => {
                const assignedToken = assignedTokens.find((token) => token.userTokenId === payload.tokenId);

                return !!assignedToken && assignedToken.rewardTokenType !== RewardTokenType.Eds;
            }),
            concatMap(([{ payload }, types]): Action[] => {
                const context = getTokenSelectionContext(payload.tokenId, types);

                if (!context || (context.betslipType === payload.betslipType && context.pickId === payload.pickId)) {
                    return [];
                }

                return [
                    RewardTokensActions.deselectToken({
                        payload: {
                            betslipType: context.betslipType,
                            pickId: context.pickId,
                            tokenId: payload.tokenId,
                        },
                    }),
                ];
            }),
        );
    });

    handleDeselectAllRewardTokenEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectAllToken),
            concatLatestFrom(() => [this.store.select(singleBetStateSelector), this.store.select(selectBetBuilderPicksState)]),
            concatMap(([_, singleBetState, betBuilder]) => {
                const singleBetActions = this.fireForAllPicks(singleBetState.picks, (pickId) =>
                    BetslipSingleBetActions.setSingleBetPickRewardToken({ pickId, rewardTokenId: null }),
                );

                const betBuilderActions = this.fireForAllPicks(betBuilder.picks, (pickId) =>
                    BetbuilderActions.setPickRewardToken({ pickId, rewardTokenId: null }),
                );

                return [
                    ...singleBetActions,
                    ...betBuilderActions,
                    BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null }),
                    BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: null }),
                    RewardTokensActions.clearRecoverableTokens(),
                ];
            }),
        );
    });

    handleRewardTokensDelayedRefreshEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.refreshPostPlacement),
            delay(this.crmConfig.promotions.postPlacementRefreshDelay),
            map(() => RewardTokensActions.refresh()),
        );
    });

    handleRewardTokensRefreshEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.refresh),
                tap(() => {
                    this.rewardTokensDataService.refreshAccaOffer();
                    this.rewardTokensDataService.refreshCrmOffer();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    cleanTokensOnLogOut$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.userLoggedOut),
            map((_) => RewardTokensActions.setTokens({ payload: [] })),
        );
    });

    // after betplacement we want to immediately remove the selected token, unless it's an accaboost, which is "reusable"
    deselectRemovableComboTokens$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectNonReusableTokens),
            concatLatestFrom(() => [
                this.store.select(selectTokensEligibilityState),
                this.store.select(selectRewardTokens),
                this.store.select(betslipTypeStateSelector),
            ]),
            filter(([_, eligibility, tokens, types]) => getSelectedAndEligibleAccaBoost(eligibility, tokens, types) === null),
            map((_) => BetslipComboBetActions.setComboBetRewardToken({ rewardTokenId: null })),
        );
    });

    deselectRemovableSingleTokens$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectNonReusableTokens),
            map((_) => BetslipSingleBetActions.removeAllSingleBetPickRewardTokens()),
        );
    });

    deselectRemovableBetBuilderTokens$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectNonReusableTokens),
            map((_) => BetbuilderActions.removeAllPickRewardTokens()),
        );
    });

    deselectRemovableTeaserTokens$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RewardTokensActions.deselectNonReusableTokens),
            map((_) => BetslipTeaserActions.setTeaserBetRewardToken({ rewardTokenId: null })),
        );
    });

    // Helper methods to avoid code duplication

    private getPickEligibilities(
        picks: Record<string, {}>,
        getInput: (pickId: PickId) => TokenValidationInput,
        tokens: IRewardToken[],
    ): { pickId: string; eligibility: IRewardTokenEligibility[] }[] {
        const pickIds = Object.keys(picks);

        return pickIds.map((pickId) => {
            const input = getInput(pickIdFactory(pickId));
            const pickEligibilityState = this.validationService.getTokenEligibilityStates(tokens, input);

            return { pickId, eligibility: pickEligibilityState };
        });
    }

    private setTokenForPick(
        pickId: PickId | undefined,
        pickState: Record<string, {}>,
        tokenId: string,
        action: (props: { pickId: string; rewardTokenId: string }) => Action,
    ): Action[] {
        const rawPickId = pickId?.toString();
        if (!rawPickId || !Object.keys(pickState).includes(rawPickId)) {
            return [];
        }

        return [
            action({
                pickId: rawPickId,
                rewardTokenId: tokenId,
            }),
        ];
    }

    private fireForAllPicks(
        picks: Record<
            string,
            {
                rewardTokenId: string | null;
            }
        >,
        action: (pickId: string) => Action,
    ): Action[] {
        return Object.keys(picks).map((pickId) => action(pickId));
    }

    private handleTokenExpiryDialog(isResultState: boolean, tokenEligibility: IRewardTokenEligibility | null): void {
        if (isResultState || tokenEligibility?.invalidHardCriteria === CriteriaType.EachWaySelected) {
            return;
        }

        this.toaster.open(this.expiredOrDeactivatedError, {
            duration: this.betslipConfig.globalErrorVisibilityTimespan,
        });
    }

    private *unselectInelgibleTokensForPicks(
        picks: Record<string, { rewardTokenId: string | null }>,
        eligibilities: Record<string, IRewardTokenEligibility[]>,
        tokens: TokensRecord,
        fireDeselectAction: (props: { pickId: string; rewardTokenId: null }) => Action,
        handleExpiryDialog: (isResultState: boolean, tokenEligibility: IRewardTokenEligibility | null) => void,
        isResultState: boolean,
    ): Iterable<Action> {
        const picksWithToken = Object.entries(picks).filter(([, pick]) => !!pick.rewardTokenId);

        for (const [pickId, { rewardTokenId }] of picksWithToken) {
            const pickEligibilities = eligibilities[pickId] ?? [];
            const { token, tokenEligibility } = getSelectedTokenState(rewardTokenId!, tokens, pickEligibilities);
            if (
                !token ||
                !tokenEligibility ||
                tokenEligibility.invalidHardCriteria === CriteriaType.ExpiryDate ||
                tokenEligibility.invalidHardCriteria === CriteriaType.EachWaySelected
            ) {
                handleExpiryDialog(!isResultState, tokenEligibility);

                yield fireDeselectAction({ pickId, rewardTokenId: null });
            }
        }
    }
}
